import * as XLSX from 'xlsx'
import type { TimeEntriesExpanded } from '@/types/timeTracking'
import {
    breakOverlapSeconds,
    getISODate,
    type Interval,
    secondsToHours,
    sumNetWorkSeconds,
    toWorkIntervals,
    workIntervalsByEntry,
} from './dates'
import {
    buildEntryRow,
    buildEntryRows,
    CSV_HEADERS_WITH_USER,
    type TagNameLookup,
} from './export-time-entries'

const BY_USER_HEADERS = [
    'User Name',
    'Date',
    'Job Name',
    'Tags',
    'Location Name',
    'Clock In',
    'Clock Out',
    'Hours',
    'Entry Type',
    'Notes',
]
const HOURS_COL = BY_USER_HEADERS.indexOf('Hours')

// buildEntryRow formats Hours as a fixed-2 string for CSV. For XLSX we want a
// real number so Excel can SUM the column and right-aligns it instead of
// flagging text-stored numbers.
function numericHours(rows: (string | number)[][], hoursCol: number): (string | number)[][] {
    return rows.map((row, idx) => {
        if (idx === 0) return row // header
        const value = row[hoursCol]
        if (value === '' || value == null) return row
        const next = [...row]
        next[hoursCol] = Number(value)
        return next
    })
}

const COMMENT_AUTHOR = 'Jobtimey'

// A break's exported Hours is the negative of only the time it overlaps work, so
// a break taken between jobs shows 0 and a partially-overlapping break shows less
// than its length. When the netted value differs from the break's own duration,
// the number alone is misleading — so we annotate the cell explaining the adjustment.
function breakAdjustmentComment(
    entry: TimeEntriesExpanded,
    workIntervals: Interval[]
): string | null {
    if (entry.entry_type !== 'break') return null
    const fullSeconds = entry.duration || 0
    if (fullSeconds === 0) return null
    const overlapSeconds = breakOverlapSeconds(entry, workIntervals)
    if (overlapSeconds >= fullSeconds) return null

    const fullHours = secondsToHours(fullSeconds).toFixed(2)
    if (overlapSeconds === 0) {
        return `This ${fullHours}h break did not overlap any work, so no time was subtracted.`
    }
    const subtracted = secondsToHours(overlapSeconds).toFixed(2)
    return `Only ${subtracted}h of this ${fullHours}h break overlapped work and was subtracted.`
}

function applyHoursComments(
    sheet: XLSX.WorkSheet,
    comments: Map<number, string>,
    hoursCol: number
): void {
    for (const [rowIdx, text] of comments) {
        const cellRef = XLSX.utils.encode_cell({ r: rowIdx, c: hoursCol })
        const cell = sheet[cellRef]
        if (!cell) continue
        cell.c = [{ a: COMMENT_AUTHOR, t: text }]
        cell.c.hidden = true
    }
}

function makeTotalRow(label: string, hours: number): (string | number)[] {
    const row: (string | number)[] = Array(BY_USER_HEADERS.length).fill('')
    row[0] = label
    row[HOURS_COL] = Number(hours.toFixed(2))
    return row
}

function entryToByUserRow(
    entry: TimeEntriesExpanded,
    workIntervals: Interval[],
    resolveTagNames?: TagNameLookup
): (string | number)[] {
    const row = buildEntryRow(entry, true, resolveTagNames, workIntervals)
    // buildEntryRow returns: [date, userName, job, tags, location, clockIn, clockOut, hours, status, entryType, notes]
    // Rearrange to: [userName, date, job, tags, location, clockIn, clockOut, hours, entryType, notes]
    return [row[1], row[0], row[2], row[3], row[4], row[5], row[6], row[7], row[9], row[10]]
}

function groupByDate(entries: TimeEntriesExpanded[]): Map<string, TimeEntriesExpanded[]> {
    const byDate = new Map<string, TimeEntriesExpanded[]>()
    for (const entry of entries) {
        if (entry.status === 'rejected') continue
        const dateKey = getISODate(entry.clock_in)
        const existing = byDate.get(dateKey)
        if (existing) existing.push(entry)
        else byDate.set(dateKey, [entry])
    }
    return byDate
}

// Accumulator threaded through the By-User builder: the growing row matrix,
// indexes of rows to bold, and Hours-cell comments keyed by row index.
interface ByUserAccumulator {
    rows: (string | number)[][]
    boldRows: number[]
    comments: Map<number, string>
}

// Appends one user's day-grouped entry rows + day/user total rows, returning the
// user's net seconds so the caller can roll up the grand total.
function appendUserRows(
    acc: ByUserAccumulator,
    userName: string,
    entries: TimeEntriesExpanded[],
    resolveTagNames?: TagNameLookup
): number {
    const byDate = groupByDate(entries)
    const sortedDates = [...byDate.keys()].sort()
    let userTotal = 0

    for (const dateKey of sortedDates) {
        const dayEntries = byDate.get(dateKey) ?? []
        dayEntries.sort((a, b) => new Date(a.clock_in).getTime() - new Date(b.clock_in).getTime())

        const dayWorkIntervals = toWorkIntervals(
            dayEntries.filter(e => e.entry_type !== 'break' && e.status !== 'rejected')
        )
        for (const entry of dayEntries) {
            acc.rows.push(entryToByUserRow(entry, dayWorkIntervals, resolveTagNames))
            const comment = breakAdjustmentComment(entry, dayWorkIntervals)
            if (comment) acc.comments.set(acc.rows.length - 1, comment)
        }

        const daySeconds = sumNetWorkSeconds(dayEntries)
        userTotal += daySeconds
        acc.rows.push(makeTotalRow(`${dateKey} Total`, secondsToHours(daySeconds)))
        acc.boldRows.push(acc.rows.length - 1)
    }

    acc.rows.push(makeTotalRow(`${userName} Total`, secondsToHours(userTotal)))
    acc.boldRows.push(acc.rows.length - 1)
    acc.rows.push(Array(BY_USER_HEADERS.length).fill(''))

    return userTotal
}

function buildByUserSheet(
    entriesByUser: Record<string, TimeEntriesExpanded[]>,
    resolveTagNames?: TagNameLookup
): XLSX.WorkSheet {
    const acc: ByUserAccumulator = {
        rows: [BY_USER_HEADERS],
        boldRows: [0],
        comments: new Map(),
    }

    const sortedUsers = Object.values(entriesByUser)
        .map(entries => ({
            name: entries[0]?.expand?.user?.name || 'Unknown',
            entries,
        }))
        .sort((a, b) => a.name.localeCompare(b.name))

    let grandTotal = 0
    for (const { name: userName, entries } of sortedUsers) {
        grandTotal += appendUserRows(acc, userName, entries, resolveTagNames)
    }

    const { rows, boldRows, comments } = acc
    rows.push(makeTotalRow('Grand Total', secondsToHours(grandTotal)))
    boldRows.push(rows.length - 1)

    const sheet = XLSX.utils.aoa_to_sheet(numericHours(rows, HOURS_COL))

    for (const rowIdx of boldRows) {
        for (let col = 0; col < BY_USER_HEADERS.length; col++) {
            const cellRef = XLSX.utils.encode_cell({ r: rowIdx, c: col })
            const cell = sheet[cellRef]
            if (cell) {
                cell.s = { font: { bold: true } }
            }
        }
    }

    applyHoursComments(sheet, comments, HOURS_COL)

    return sheet
}

// Builds a flat entries sheet (All Entries / Approved) with numeric Hours and
// break-adjustment comments. buildEntryRows maps entries 1:1 in order, so data
// rows align to entries offset by the header row.
function buildFlatSheet(
    entries: TimeEntriesExpanded[],
    headers: string[],
    hoursCol: number,
    resolveTagNames?: TagNameLookup
): XLSX.WorkSheet {
    const rows = numericHours(
        [headers, ...buildEntryRows(entries, true, resolveTagNames)],
        hoursCol
    )
    const sheet = XLSX.utils.aoa_to_sheet(rows)

    const intervalsByEntry = workIntervalsByEntry(entries)
    const comments = new Map<number, string>()
    entries.forEach((entry, idx) => {
        const comment = breakAdjustmentComment(entry, intervalsByEntry.get(entry.id) ?? [])
        if (comment) comments.set(idx + 1, comment) // +1 for header row
    })
    applyHoursComments(sheet, comments, hoursCol)

    return sheet
}

interface GenerateXLSXOptions {
    entriesByUser?: Record<string, TimeEntriesExpanded[]>
    resolveTagNames?: TagNameLookup
}

export function generateTimeEntriesXLSX(
    entries: TimeEntriesExpanded[],
    options: GenerateXLSXOptions = {}
): Uint8Array {
    const { resolveTagNames } = options
    const headers = CSV_HEADERS_WITH_USER
    const hoursCol = headers.indexOf('Hours')

    const approvedEntries = entries.filter(e => e.status === 'approved')

    const workbook = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(
        workbook,
        buildFlatSheet(entries, headers, hoursCol, resolveTagNames),
        'All Entries'
    )
    XLSX.utils.book_append_sheet(
        workbook,
        buildFlatSheet(approvedEntries, headers, hoursCol, resolveTagNames),
        'Approved'
    )

    if (options.entriesByUser) {
        XLSX.utils.book_append_sheet(
            workbook,
            buildByUserSheet(options.entriesByUser, resolveTagNames),
            'By User'
        )
    }

    return XLSX.write(workbook, { type: 'array', bookType: 'xlsx' }) as Uint8Array
}
